import React, { Dispatch } from 'react'
import { VisuallyHidden } from '@overdose/components'
import {
  IconUser,
  IconPlaylistAdd,
  IconShoppingCart,
  IconShoppingBag,
} from '@tabler/icons-react'
import classNames from 'classnames'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { Button } from '~/components'
import { HintTooltip } from '~/components/HintTooltip'
import {
  useCart,
  useCartDrawer,
  useCustomer,
  useCustomerListsDrawer,
} from '~/hooks'
import styles from './Header.module.css'

const whiteColor = 'var(--color-grayscale-white)'
const iconButtonStyles = {
  '--btn-size-md-height': '32px',
  '--btn-ghost-color-text-primary': whiteColor,
}

const Badge = dynamic(
  async () => {
    const mod = await import('@overdose/components')
    return mod.Badge
  },
  {
    ssr: false,
    loading: () => {
      return <IconShoppingBag />
    },
  }
)

const buttonStyle = {
  '--btn-icon-size': '32px',
  ...iconButtonStyles,
} as React.CSSProperties

interface MenuIconsProps {
  setOpenMegaMenu: Dispatch<React.SetStateAction<boolean>>
  handleAccountViewer: () => void
  showCart?: boolean
}

const MenuIcons: React.FC<MenuIconsProps> = ({
  setOpenMegaMenu,
  handleAccountViewer,
  showCart = false,
}) => {
  const { openDrawer } = useCustomerListsDrawer()
  const router = useRouter()
  const { customer } = useCustomer()
  const { cart } = useCart()
  const { openCartDrawer } = useCartDrawer()

  return (
    <div
      className={classNames(
        styles.iconWrapper,
        'text-white flex gap-2.5 lg:gap-6 2xl:gap-8'
      )}>
      <HintTooltip message='My Account'>
        <Button
          icon={<IconUser size={32} />}
          iconOnly
          onClick={() => {
            setOpenMegaMenu(false)
            handleAccountViewer()
          }}
          shape='circle'
          style={buttonStyle}
          variant='ghost'>
          <VisuallyHidden>My Account</VisuallyHidden>
        </Button>
      </HintTooltip>
      <HintTooltip message='My Lists'>
        <Button
          icon={<IconPlaylistAdd size={32} />}
          iconOnly
          onClick={() => {
            setOpenMegaMenu(false)
            if (customer) {
              openDrawer('')
            } else {
              router.push({ hash: 'login' })
            }
          }}
          shape='circle'
          style={buttonStyle}
          variant='ghost'>
          <VisuallyHidden>My Lists</VisuallyHidden>
        </Button>
      </HintTooltip>
      {showCart && (
        <HintTooltip message='Cart'>
          <Button
            tooltip='Cart'
            data-id='viewCartButton'
            icon={
              <Badge
                count={cart?.totalLineItemQuantity}
                variant='count'
                isRounded
                theme={{ root: styles.countBadge, number: 'w-6 h-6' }}>
                <IconShoppingCart size={32} />
              </Badge>
            }
            iconOnly
            onClick={() => {
              setOpenMegaMenu(false)
              openCartDrawer()
            }}
            shape='circle'
            style={buttonStyle}
            variant='ghost'>
            <VisuallyHidden>Cart</VisuallyHidden>
          </Button>
        </HintTooltip>
      )}
    </div>
  )
}

export default MenuIcons
